import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { AccountAbility } from './account-ability.model';

@Injectable()
export class AclService {
  private accountAbility: AccountAbility;
  private fetchedAccountAbility = false;

  // https://stackoverflow.com/a/43351340
  // need to use BehaviorSubject, since it will hold at least one value and paste it to late subscribers
  // Otherwise, late subscribers won't see the ability list. Ex: Sale components & services are initialized later in code.
  private accountAbilityState = new BehaviorSubject<any>([]);

  constructor(private http: HttpClient) {}

  fetch(): Observable<HttpResponse<AccountAbility>> {
    return this.http.get<AccountAbility>('/hrm-portal-api/api/users/permissions/', { observe: 'response' });
  }

  hasAnyPermission(permissions: string[]): boolean {
    if (!this.fetchedAccountAbility || !this.accountAbility || !this.accountAbility.permissions) {
      return false;
    }

    return permissions.some((authority: string) => this.accountAbility.permissions.includes(authority));
  }

  hasPermission(authority: string): Promise<boolean> {
    if (!this.fetchedAccountAbility) {
      return Promise.resolve(false);
    }

    return this.canAbility().then(
      id => {
        return Promise.resolve(id.permissions && id.permissions.includes(authority));
      },
      () => {
        return Promise.resolve(false);
      }
    );
  }

  hasAnyFunction(functions: string[]): boolean {
    if (!functions || functions.length === 0) {
      return true;
    }

    if (!this.fetchedAccountAbility || !this.accountAbility || !this.accountAbility.functions) {
      return false;
    }

    return functions.some((authority: string) => this.accountAbility.functions.includes(authority));
  }

  hasFunction(authority: string): Promise<boolean> {
    if (!this.fetchedAccountAbility) {
      return Promise.resolve(false);
    }

    return this.canAbility().then(
      id => {
        return Promise.resolve(id.functions && id.functions.includes(authority));
      },
      () => {
        return Promise.resolve(false);
      }
    );
  }

  /**
   * Does the current user have corresponding authority points.
   *
   * @param {boolean} [force]
   * @returns {Promise<AccountAbility>}
   * @memberof AclService
   */
  canAbility(force?: boolean): Promise<AccountAbility> {
    if (force) {
      this.accountAbility = undefined;
    }

    // check and see if we have retrieved the accountAbility data from the server.
    // if we have, reuse it by immediately resolving
    if (this.accountAbility) {
      return Promise.resolve(this.accountAbility);
    }

    // retrieve the accountAbility data from the server, update the identity object, and then resolve.
    return this.fetch()
      .toPromise()
      .then(response => {
        const accountAbility: AccountAbility = response.body;
        if (accountAbility) {
          this.accountAbility = accountAbility;
          this.fetchedAccountAbility = true;
        } else {
          this.accountAbility = null;
          this.fetchedAccountAbility = false;
        }
        this.accountAbilityState.next(this.accountAbility);
        return this.accountAbility;
      })
      .catch(err => {
        this.accountAbility = null;
        this.fetchedAccountAbility = false;
        this.accountAbilityState.next(this.accountAbility);
        return null;
      });
  }

  isFetchedAccountAbility(): boolean {
    return this.fetchedAccountAbility;
  }

  isAccountAbilityResolved(): boolean {
    return this.accountAbility !== undefined;
  }

  getAccountAbilityState(): Observable<any> {
    return this.accountAbilityState.asObservable();
  }
}
